import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../authorize/AuthContext";
import { getGoogleAuthUrl } from "./googleAuth";
import AuthHeader from "./AuthHeader"; // ✅ Header (unchanged)

const EyeIcon = ({ closed = false }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
    <circle cx="12" cy="12" r="3" />
    {closed ? <path d="M4 4l16 16" /> : null}
  </svg>
);

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  const handleGoogleLogin = () => {
    try {
      window.location.href = getGoogleAuthUrl();
    } catch (error) {
      Swal.fire({
        title: "Google Sign-in Unavailable",
        text: error?.message || "Google sign-in is not configured correctly.",
        icon: "error",
        confirmButtonColor: "#2563eb",
      });
    }
  };

  useEffect(() => {
    const loggedIn = localStorage.getItem("isLoggedIn");
    if (loggedIn === "true") navigate("/dashboard");
  }, [navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      Swal.fire({
        title: "Error!",
        text: "Please input your username and password",
        icon: "error",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "Back",
      });
      return;
    }

    try {
      const response = await fetch(`${roboman_api}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("access_token", data.access_token);
        login();
        navigate("/dashboard");
      } else {
        const errorData = await response.json();
        const messages = Array.isArray(errorData.detail)
          ? errorData.detail.map((item) => item.msg).join(", ")
          : errorData.detail;

        Swal.fire({
          title: "Error!",
          text: messages,
          icon: "error",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "Close",
        });
      }
    } catch {
      Swal.fire({
        title: "Error!",
        text: "An error occurred. Please try again.",
        icon: "error",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "Back",
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white overflow-x-hidden">
      <AuthHeader />

      {/* Light, airy background */}
      <div className="relative isolate flex-1 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50 via-white to-fuchsia-50" />

        {/* Lighter, contained glows */}
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-fuchsia-300/20 blur-3xl" />

        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-0 px-4 sm:px-6 lg:px-8">
          {/* Left brand panel — converted to light card */}
          <aside className="relative hidden lg:flex lg:col-span-6 items-center py-12">
            <div className="w-full rounded-3xl bg-white text-slate-800 ring-1 ring-slate-200 shadow-xl overflow-hidden">
              <div className="relative p-10 sm:p-12">
                {/* subtle light pattern */}
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, rgba(2,6,23,.06) 1px, transparent 0)",
                    backgroundSize: "22px 22px",
                  }}
                />
                <div className="relative">
                  <p className="text-xs tracking-[.25em] font-semibold uppercase text-fuchsia-600/70">
                    Roboman
                  </p>
                  <h1 className="mt-2 text-4xl font-extrabold leading-tight text-slate-900">
                    Welcome back to your outreach cockpit
                  </h1>
                  <p className="mt-3 text-slate-600 max-w-xl">
                    Orchestrate email sequences, LinkedIn steps, multilingual voice notes, and proposal generation — all from one place.
                  </p>

                  {/* capability chips — light surface */}
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["AI Emails", "LinkedIn", "Voice Notes", "Proposals", "Intent Scoring"].map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs ring-1 ring-slate-200 text-slate-700"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Right form card — already light, kept consistent */}
          <main className="lg:col-span-6 flex items-center py-12">
            <div className="w-full mx-auto max-w-md">
              <div className="relative rounded-3xl bg-white/90 backdrop-blur-xl shadow-2xl ring-1 ring-slate-200">
                <div className="h-[3px] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />

                <div className="p-8 sm:p-10">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900">Sign in</h2>
                    <p className="mt-1 text-sm text-slate-600">Access your account</p>
                  </div>

                  <form className="mt-6 space-y-4" onSubmit={handleLogin}>
                    <label className="block">
                      <span className="sr-only">Email address</span>
                      <input
                        type="email"
                        placeholder="Work email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        autoComplete="email"
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="sr-only">Password</span>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoComplete="current-password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-600"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <EyeIcon closed={showPassword} />
                        </button>
                      </div>
                    </label>

                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500" />
                      <button
                        type="button"
                        onClick={() => navigate("/forgotpw")}
                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl px-5 py-3 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
                      <span className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(100%_100%_at_50%_0%,rgba(255,255,255,.35)_0,rgba(255,255,255,0)_60%)]" />
                      <span className="relative">Sign In</span>
                    </button>
                  </form>

                  <div className="my-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs text-slate-500">or continue with</span>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>

                  <div className="flex justify-center gap-4">
                    {/* Google (unchanged flow) */}
                    <button
                      type="button"
                      // disabled
                      // onClick={() => {
                      //   const url = `${roboman_api}/auth/google/login`;
                      //   console.log(`${roboman_api}/auth/google/login`)
                      //   window.location.href = url;
                      // }}

                      onClick={handleGoogleLogin}

                      className="h-11 w-11 rounded-full ring-1 ring-slate-200 bg-white shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      aria-label="Sign in with Google"
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/300/300221.png"
                        alt="Google"
                        className="mx-auto h-5 w-5"
                      />
                    </button>

                    {/* LinkedIn / Microsoft (disabled, unchanged) */}
                    <button
                      type="button"
                      // disabled
                      onClick={() => (window.location.href = `${roboman_api}/login/linkedin`)}
                      className="h-11 w-11 rounded-full ring-1 ring-slate-200 bg-white shadow-sm opacity-60 cursor-not-allowed"
                      aria-label="Sign in with LinkedIn"
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/174/174857.png"
                        alt="LinkedIn"
                        className="mx-auto h-5 w-5"
                      />
                    </button>

                    <button
                      type="button"
                      // disabled
                      onClick={() => (window.location.href = `${roboman_api}/login/microsoft`)}
                      className="h-11 w-11 rounded-full ring-1 ring-slate-200 bg-white shadow-sm opacity-60 cursor-not-allowed"
                      aria-label="Sign in with Microsoft"
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/732/732221.png"
                        alt="Microsoft"
                        className="mx-auto h-5 w-5"
                      />
                    </button>
                  </div>

                  <p className="mt-6 text-center text-sm text-slate-600">
                    Don’t have an account?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/signup")}
                      className="font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Sign Up
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Login;
